import { IonCol, IonRow, IonSearchbar } from '@ionic/react';
import { useCallback, useState } from 'react';
import { DataTable } from '.';
import { useDebounceWithIdle } from 'src/hooks/useDebounce';
import { DataTablePagination } from './interfaces/DataTablePagination';
import { DataTableRow } from './interfaces/DataTableRow';
import { DataTableHeader } from './interfaces/DataTableHeader';
import { DataTableItemAction } from './interfaces/DataTableItemAction';

interface DataTableWithSearchProps {
    title?: string;
    onPaginationChange: (pagination: DataTablePagination) => void;
    orderBy?: string | null;
    orderByDesc?: boolean;
    setOrderBy?: (value: string | null) => void;
    setOrderByDesc?: (value: boolean) => void;
    totalCount: number;
    headers: DataTableHeader[];
    itemsPerPageOptions?: number[];
    itemsPerPage?: number;
    hidePagination?: boolean;
    loading?: boolean;
    onSearchChange: (value: string | null) => void;
    rows: DataTableRow[];
    children?: React.ReactNode;
    subtitle?: any;
    innerSearch?: boolean;
    actions?: (item: any) => DataTableItemAction[];
    rowCssClasses?: string
    searchColumnProps?: {
        size?: string;
        sizeSm?: string;
        sizeMd?: string;
        sizeLg?: string;
        sizeXl?: string;
        offset?: string;
        offsetSm?: string;
        offsetMd?: string;
        offsetLg?: string;
        offsetXl?: string;
    };
}

export const DataTableWithSearch: React.FC<DataTableWithSearchProps> = ({
    title,
    subtitle,
    onPaginationChange,
    orderBy,
    orderByDesc,
    setOrderBy,
    setOrderByDesc,
    totalCount,
    headers,
    itemsPerPageOptions,
    itemsPerPage,
    actions,
    hidePagination,
    loading,
    onSearchChange,
    rows,
    children,
    innerSearch = false,
    searchColumnProps = {
        size: "12",
        sizeSm: "8"
    },
    rowCssClasses
}) => {
    const [searchInput, setSearchInput] = useState<string>('');

    const updateSearch = useCallback(
        (value: string) => {
            onSearchChange(value || null);
        },
        [onSearchChange]
    );

    const debouncedSearch = useDebounceWithIdle(updateSearch, 500);

    const handleSearchInput = (value: string) => {
        setSearchInput(value);
        debouncedSearch(value);
    };

    return (
        <DataTable
            title={title}
            onPaginationChange={onPaginationChange}
            orderBy={orderBy}
            orderByDesc={orderByDesc}
            setOrderBy={setOrderBy}
            setOrderByDesc={setOrderByDesc}
            totalCount={totalCount}
            headers={headers}
            itemsPerPageOptions={itemsPerPageOptions}
            itemsPerPage={itemsPerPage}
            hidePagination={hidePagination}
            loading={loading}
            search={innerSearch}
            rows={rows}
            subtitle={subtitle}
            actions={actions}
        >
            <IonRow className={rowCssClasses}>
                {children}
                <IonCol
                    size={searchColumnProps.size}
                    sizeSm={searchColumnProps.sizeSm}
                    sizeMd={searchColumnProps.sizeMd}
                    sizeLg={searchColumnProps.sizeLg}
                    sizeXl={searchColumnProps.sizeXl}
                    offset={searchColumnProps.offset}
                    offsetSm={searchColumnProps.offsetSm}
                    offsetMd={searchColumnProps.offsetMd}
                    offsetLg={searchColumnProps.offsetLg}
                    offsetXl={searchColumnProps.offsetXl}
                >
                    <IonSearchbar
                        value={searchInput}
                        onIonChange={(e) => handleSearchInput(e.detail.value as string)}
                        className="ion-text-left"
                        placeholder="Search"
                        debounce={0}
                    />
                </IonCol>
            </IonRow>
        </DataTable>
    );
}; 